import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import type { Benutzer } from '@beholder/shared';
import type { MindmapKnoten } from '@beholder/shared/module/mindmap';
import { baueBaum, nachkommen, zwischenwert } from './baum';
import { anordnen, kante, MASS, type Lage } from './layout';
import { FARBTON, KLAMMER_PFAD, NOTIZ_PFAD, personFarbe, SYMBOL_PFAD } from './symbole';
import type { Befehl } from './TabellenAnsicht';
import { neueId, type KartenZugriff } from './useKarte';

interface Sicht {
  x: number;
  y: number;
  s: number;
}

interface Bearbeitung {
  id: string;
  text: string;
  basis: string;
  neu?: { elternId: string; index: number };
}

interface Ziehen {
  id: string;
  zeiger: number;
  sx: number;
  sy: number;
  aktiv: boolean;
  wx: number;
  wy: number;
  dx: number;
  dy: number;
  ziel: { id: string; art: 'kind' | 'davor' | 'danach' } | null;
}

export interface MindmapAnsichtProps {
  z: KartenZugriff;
  zu: Set<string>;
  umschalten(id: string, an?: boolean): void;
  auswahl: string | null;
  waehlen(id: string | null, oeffnen?: boolean): void;
  darfSchreiben: boolean;
  ich: Benutzer;
  loeschenFragen(id: string): void;
  bearbeitetMelden(an: boolean): void;
  befehl: Befehl | null;
}

const MIN_S = 0.15;
const MAX_S = 2.5;

export function MindmapAnsicht({ z, zu, umschalten, auswahl, waehlen, darfSchreiben, ich, loeschenFragen, bearbeitetMelden, befehl }: MindmapAnsichtProps) {
  const rahmen = useRef<HTMLDivElement>(null);
  const [groesse, setGroesse] = useState({ b: 800, h: 600 });
  const [sicht, setSicht] = useState<Sicht>({ x: 400, y: 300, s: 1 });
  const sichtRef = useRef(sicht);
  sichtRef.current = sicht;
  const [bearb, setBearbState] = useState<Bearbeitung | null>(null);
  const bearbRef = useRef<Bearbeitung | null>(null);
  const setBearb = (b: Bearbeitung | null) => {
    bearbRef.current = b;
    setBearbState(b);
    bearbeitetMelden(!!b);
  };
  const [ziehen, setZiehen] = useState<Ziehen | null>(null);
  const zeiger = useRef(new Map<number, { x: number; y: number }>());
  const schwenk = useRef<{ x: number; y: number; sx: number; sy: number; abstand?: number; s?: number; mx?: number; my?: number; bewegt: boolean } | null>(null);
  const eingepasst = useRef('');

  // Größe des Rahmens verfolgen
  useEffect(() => {
    const el = rahmen.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setGroesse({ b: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setGroesse({ b: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  // Baum für die Anordnung – mit Entwurf und laufender Bearbeitung
  const baum = useMemo(() => {
    if (!bearb) return z.baum;
    const liste: MindmapKnoten[] = Object.values(z.knoten).map((k) => (k.id === bearb.id ? { ...k, titel: bearb.text || ' ' } : k));
    if (bearb.neu) {
      liste.push({
        id: bearb.id,
        karteId: '',
        elternId: bearb.neu.elternId,
        reihenfolge: zwischenwert(z.baum, bearb.neu.elternId, bearb.neu.index),
        titel: bearb.text || ' ',
        beschreibung: '',
        farbe: null,
        symbol: null,
        werte: {},
        anlagen: 0,
        notizen: 0,
        version: 0,
        geaendertVon: null,
        geaendertVonName: null,
        geaendertAm: '',
      });
    }
    return baueBaum(liste);
  }, [z.baum, z.knoten, bearb]);

  const anordnung = useMemo(() => anordnen(baum, zu), [baum, zu]);

  const einpassen = useCallback(() => {
    const g = anordnung.grenzen;
    const w = g.x2 - g.x1;
    const h = g.y2 - g.y1;
    if (!w || !h) return;
    const s = Math.max(MIN_S, Math.min(1.1, (groesse.b - 32) / w, (groesse.h - 32) / h));
    setSicht({ s, x: groesse.b / 2 - ((g.x1 + g.x2) / 2) * s, y: groesse.h / 2 - ((g.y1 + g.y2) / 2) * s });
  }, [anordnung, groesse]);

  // Einpassen, solange der Nutzer die Ansicht noch nicht selbst verschoben oder gezoomt hat
  // (auch wenn sich die Fenstergröße nach dem Laden noch ändert)
  const selbstBewegt = useRef(false);
  useEffect(() => {
    const id = z.karte?.id ?? '';
    if (!id || !anordnung.liste.length || groesse.b < 50 || groesse.h < 50) return;
    const schluessel = `${id}|${groesse.b}|${groesse.h}`;
    if (eingepasst.current === schluessel || selbstBewegt.current) return;
    const erstes = !eingepasst.current.startsWith(`${id}|`);
    eingepasst.current = schluessel;
    if (!erstes) {
      // Nur neu einpassen, wenn nach der Größenänderung etwas abgeschnitten wäre
      const g = anordnung.grenzen;
      const v = sichtRef.current;
      const passt = g.x1 * v.s + v.x >= 0 && g.y1 * v.s + v.y >= 0 && g.x2 * v.s + v.x <= groesse.b && g.y2 * v.s + v.y <= groesse.h;
      if (passt) return;
    }
    einpassen();
  }, [z.karte?.id, anordnung, groesse, einpassen]);

  const zoomen = useCallback((faktor: number, px?: number, py?: number) => {
    selbstBewegt.current = true;
    setSicht((v) => {
      const s = Math.max(MIN_S, Math.min(MAX_S, v.s * faktor));
      const cx = px ?? rahmen.current!.clientWidth / 2;
      const cy = py ?? rahmen.current!.clientHeight / 2;
      return { s, x: cx - ((cx - v.x) * s) / v.s, y: cy - ((cy - v.y) * s) / v.s };
    });
  }, []);

  // Mausrad = zoomen (nicht passiv, damit die Seite nicht scrollt)
  useEffect(() => {
    const el = rahmen.current;
    if (!el) return;
    const h = (e: WheelEvent) => {
      e.preventDefault();
      selbstBewegt.current = true;
      const r = el.getBoundingClientRect();
      if (e.shiftKey && !e.ctrlKey) {
        setSicht((v) => ({ ...v, x: v.x - e.deltaY - e.deltaX }));
        return;
      }
      zoomen(Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0015)), e.clientX - r.left, e.clientY - r.top);
    };
    el.addEventListener('wheel', h, { passive: false });
    return () => el.removeEventListener('wheel', h);
  }, [zoomen]);

  // Ausgewählten Knoten sichtbar halten
  const sichtbarMachen = useCallback(
    (id: string) => {
      const l = anordnung.lagen.get(id);
      if (!l) return;
      const v = sichtRef.current;
      const x1 = l.x * v.s + v.x;
      const y1 = l.y * v.s + v.y;
      const x2 = x1 + l.b * v.s;
      const y2 = y1 + l.h * v.s;
      let dx = 0;
      let dy = 0;
      const rand = 40;
      if (x1 < rand) dx = rand - x1;
      else if (x2 > groesse.b - rand) dx = groesse.b - rand - x2;
      if (y1 < rand) dy = rand - y1;
      else if (y2 > groesse.h - rand) dy = groesse.h - rand - y2;
      if (dx || dy) setSicht({ ...v, x: v.x + dx, y: v.y + dy });
    },
    [anordnung, groesse],
  );

  useEffect(() => {
    if (bearb) sichtbarMachen(bearb.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bearb?.id, anordnung]);

  // ------------------------------------------------------------ Bearbeiten

  const neuerEntwurf = (elternId: string, index: number) => {
    if (!darfSchreiben) return;
    if (zu.has(elternId)) umschalten(elternId, false);
    setBearb({ id: neueId(), text: '', basis: '', neu: { elternId, index } });
  };

  const umbenennenStarten = (id: string, text?: string) => {
    if (!darfSchreiben) return;
    const k = z.knoten[id];
    if (!k) return;
    setBearb({ id, text: text ?? k.titel, basis: k.titel });
  };

  const fertig = (weiter?: 'kind' | 'geschwister') => {
    const b = bearbRef.current;
    if (!b) return;
    const t = b.text.trim();
    let naechster: Bearbeitung | null = null;
    const entwurf = (elternId: string, index: number): Bearbeitung => ({ id: neueId(), text: '', basis: '', neu: { elternId, index } });
    if (b.neu) {
      if (t) {
        void z.anlegen(b.neu.elternId, b.neu.index, t, {}, b.id);
        waehlen(b.id);
        if (weiter === 'kind') naechster = entwurf(b.id, 0);
        if (weiter === 'geschwister') naechster = entwurf(b.neu.elternId, b.neu.index + 1);
      }
    } else {
      if (t && t !== b.basis) void z.aendern(b.id, { titel: t }, { titel: b.basis });
      if (weiter === 'kind') naechster = entwurf(b.id, (z.baum.kinder.get(b.id) ?? []).length);
    }
    setBearb(naechster);
    if (!naechster) rahmen.current?.focus({ preventScroll: true });
  };

  useEffect(() => {
    if (!befehl || !z.knoten[befehl.id]) return;
    if (befehl.art === 'kind') neuerEntwurf(befehl.id, (z.baum.kinder.get(befehl.id) ?? []).length);
    else geschwisterEntwurf(befehl.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [befehl?.nr]);

  const abbrechen = () => {
    setBearb(null);
    rahmen.current?.focus({ preventScroll: true });
  };

  const geschwisterEntwurf = (id: string) => {
    const k = z.knoten[id];
    if (!k) return;
    if (!k.elternId) return neuerEntwurf(id, (z.baum.kinder.get(id) ?? []).length);
    const liste = z.baum.kinder.get(k.elternId) ?? [];
    neuerEntwurf(k.elternId, liste.findIndex((x) => x.id === id) + 1);
  };

  // ------------------------------------------------------------ Tastatur

  const nachbar = (id: string, richtung: 'hoch' | 'runter' | 'links' | 'rechts'): string | null => {
    const l = anordnung.lagen.get(id);
    const k = z.knoten[id];
    if (!l || !k) return null;
    const zumKind = (seite: 1 | -1) => {
      if (l.eingeklappt) return null;
      const kinder = (baum.kinder.get(id) ?? []).map((c) => anordnung.lagen.get(c.id)).filter((x): x is Lage => !!x && (l.seite === 0 ? x.seite === seite : true));
      if (!kinder.length) return null;
      const mitte = l.y + l.h / 2;
      return kinder.reduce((a, b) => (Math.abs(b.y + b.h / 2 - mitte) < Math.abs(a.y + a.h / 2 - mitte) ? b : a)).id;
    };
    if (richtung === 'rechts') return l.seite === -1 ? k.elternId : zumKind(1);
    if (richtung === 'links') return l.seite === 1 ? k.elternId : zumKind(-1);
    const kandidaten = anordnung.liste.filter((x) => x.id !== id && x.tiefe === l.tiefe && x.seite === l.seite);
    const mitte = l.y + l.h / 2;
    const passend = kandidaten.filter((x) => (richtung === 'hoch' ? x.y + x.h / 2 < mitte : x.y + x.h / 2 > mitte));
    if (!passend.length) return null;
    return passend.reduce((a, b) => (Math.abs(b.y + b.h / 2 - mitte) < Math.abs(a.y + a.h / 2 - mitte) ? b : a)).id;
  };

  const taste = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (bearbRef.current || e.target !== rahmen.current) return;
    const id = auswahl ?? z.baum.wurzel?.id ?? null;
    if (!id) return;
    const k = z.knoten[id];
    const geh = (n: string | null) => {
      if (n) {
        waehlen(n);
        sichtbarMachen(n);
      }
    };
    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        if (e.ctrlKey || e.metaKey) waehlen(id, true);
        else geschwisterEntwurf(id);
        return;
      case 'Tab':
      case 'Insert':
        e.preventDefault();
        neuerEntwurf(id, (z.baum.kinder.get(id) ?? []).length);
        return;
      case 'Delete':
      case 'Backspace':
        e.preventDefault();
        if (darfSchreiben && k?.elternId) loeschenFragen(id);
        return;
      case 'F2':
        e.preventDefault();
        umbenennenStarten(id);
        return;
      case ' ':
        e.preventDefault();
        if (k?.elternId && z.baum.kinder.get(id)?.length) umschalten(id);
        return;
      case 'Escape':
        waehlen(null);
        return;
      case 'ArrowUp':
        e.preventDefault();
        return geh(nachbar(id, 'hoch'));
      case 'ArrowDown':
        e.preventDefault();
        return geh(nachbar(id, 'runter'));
      case 'ArrowLeft':
        e.preventDefault();
        return geh(nachbar(id, 'links'));
      case 'ArrowRight':
        e.preventDefault();
        return geh(nachbar(id, 'rechts'));
      case '+':
        return zoomen(1.2);
      case '-':
        return zoomen(1 / 1.2);
      case '0':
        return einpassen();
    }
    // Tippen beginnt das Umbenennen (wie in einer Tabelle)
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && auswahl) {
      e.preventDefault();
      umbenennenStarten(id, e.key);
    }
  };

  // ------------------------------------------------------------ Zeiger (schwenken, ziehen, zoomen)

  const welt = (cx: number, cy: number) => {
    const r = rahmen.current!.getBoundingClientRect();
    const v = sichtRef.current;
    return { x: (cx - r.left - v.x) / v.s, y: (cy - r.top - v.y) / v.s };
  };

  const zielFinden = (wx: number, wy: number, id: string): Ziehen['ziel'] => {
    const aus = new Set([id, ...nachkommen(z.baum, id)]);
    for (const l of anordnung.liste) {
      if (aus.has(l.id)) continue;
      if (wx < l.x - 6 || wx > l.x + l.b + 6 || wy < l.y - 8 || wy > l.y + l.h + 8) continue;
      if (l.seite === 0) return { id: l.id, art: 'kind' };
      const rel = (wy - l.y) / l.h;
      return { id: l.id, art: rel < 0.28 ? 'davor' : rel > 0.72 ? 'danach' : 'kind' };
    }
    return null;
  };

  const runter = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (bearbRef.current) fertig();
    rahmen.current?.focus({ preventScroll: true });
    const klapp = (e.target as Element).closest('[data-klapp]');
    if (klapp) {
      const kid = klapp.closest('[data-knoten]')?.getAttribute('data-knoten');
      if (kid) umschalten(kid);
      return;
    }
    zeiger.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const ziel = (e.target as Element).closest('[data-knoten]');
    const id = ziel?.getAttribute('data-knoten') ?? null;
    if (zeiger.current.size === 2) {
      // Zwei Finger: zoomen
      const [a, b] = [...zeiger.current.values()];
      const v = sichtRef.current;
      schwenk.current = {
        x: 0,
        y: 0,
        sx: v.x,
        sy: v.y,
        abstand: Math.hypot(a!.x - b!.x, a!.y - b!.y),
        s: v.s,
        mx: (a!.x + b!.x) / 2,
        my: (a!.y + b!.y) / 2,
        bewegt: true,
      };
      setZiehen(null);
      return;
    }
    const k = id ? z.knoten[id] : null;
    if (id && k && darfSchreiben && e.pointerType !== 'touch' && k.elternId && e.button === 0) {
      const w = welt(e.clientX, e.clientY);
      const l = anordnung.lagen.get(id)!;
      setZiehen({ id, zeiger: e.pointerId, sx: e.clientX, sy: e.clientY, aktiv: false, wx: w.x, wy: w.y, dx: w.x - l.x, dy: w.y - l.y, ziel: null });
      return;
    }
    schwenk.current = { x: e.clientX, y: e.clientY, sx: sichtRef.current.x, sy: sichtRef.current.y, bewegt: false };
    (schwenk.current as { knoten?: string | null }).knoten = id;
  };

  const bewegen = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (!zeiger.current.has(e.pointerId)) return;
    zeiger.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const sw = schwenk.current;
    if (sw?.abstand && zeiger.current.size >= 2) {
      selbstBewegt.current = true;
      const [a, b] = [...zeiger.current.values()];
      const r = rahmen.current!.getBoundingClientRect();
      const abstand = Math.hypot(a!.x - b!.x, a!.y - b!.y);
      const s = Math.max(MIN_S, Math.min(MAX_S, sw.s! * (abstand / sw.abstand)));
      const mx = (a!.x + b!.x) / 2 - r.left;
      const my = (a!.y + b!.y) / 2 - r.top;
      const ax = sw.mx! - r.left;
      const ay = sw.my! - r.top;
      setSicht({ s, x: mx - ((ax - sw.sx) * s) / sw.s!, y: my - ((ay - sw.sy) * s) / sw.s! });
      return;
    }
    if (ziehen && ziehen.zeiger === e.pointerId) {
      const weit = Math.hypot(e.clientX - ziehen.sx, e.clientY - ziehen.sy) > 5;
      const w = welt(e.clientX, e.clientY);
      if (!ziehen.aktiv && weit) e.currentTarget.setPointerCapture?.(e.pointerId);
      if (ziehen.aktiv || weit) setZiehen({ ...ziehen, aktiv: true, wx: w.x, wy: w.y, ziel: zielFinden(w.x, w.y, ziehen.id) });
      return;
    }
    if (sw && !sw.abstand) {
      const dx = e.clientX - sw.x;
      const dy = e.clientY - sw.y;
      if (!sw.bewegt && Math.abs(dx) + Math.abs(dy) > 3) {
        sw.bewegt = true;
        selbstBewegt.current = true;
        e.currentTarget.setPointerCapture?.(e.pointerId);
      }
      if (sw.bewegt) setSicht((v) => ({ ...v, x: sw.sx + dx, y: sw.sy + dy }));
    }
  };

  const hoch = (e: ReactPointerEvent<SVGSVGElement>) => {
    zeiger.current.delete(e.pointerId);
    const sw = schwenk.current as (typeof schwenk.current & { knoten?: string | null }) | null;
    if (ziehen && ziehen.zeiger === e.pointerId) {
      setZiehen(null);
      if (!ziehen.aktiv) {
        waehlen(ziehen.id, e.pointerType === 'touch');
        return;
      }
      const ziel = ziehen.ziel;
      if (!ziel) return;
      const zk = z.knoten[ziel.id];
      if (!zk) return;
      if (ziel.art === 'kind') {
        const n = (z.baum.kinder.get(ziel.id) ?? []).filter((x) => x.id !== ziehen.id).length;
        if (zu.has(ziel.id)) umschalten(ziel.id, false);
        void z.verschieben(ziehen.id, ziel.id, n);
      } else if (zk.elternId) {
        const liste = (z.baum.kinder.get(zk.elternId) ?? []).filter((x) => x.id !== ziehen.id);
        const i = liste.findIndex((x) => x.id === ziel.id);
        void z.verschieben(ziehen.id, zk.elternId, ziel.art === 'davor' ? i : i + 1);
      }
      return;
    }
    if (zeiger.current.size === 0) {
      if (sw && !sw.bewegt && !sw.abstand) {
        if (sw.knoten) waehlen(sw.knoten, e.pointerType === 'touch');
        else waehlen(null);
      }
      schwenk.current = null;
    } else if (zeiger.current.size === 1 && sw?.abstand) {
      const [rest] = [...zeiger.current.values()];
      schwenk.current = { x: rest!.x, y: rest!.y, sx: sichtRef.current.x, sy: sichtRef.current.y, bewegt: true };
    }
  };

  // ------------------------------------------------------------ Zeichnen

  const andere = z.anwesend.filter((p) => p.benutzerId !== ich.id && p.knotenId);
  const ton = (id: string) => {
    const f = baum.astFarbe.get(id);
    return f ? FARBTON[f] : 'var(--akzent)';
  };

  const bl = bearb ? anordnung.lagen.get(bearb.id) : undefined;

  return (
    <div
      ref={rahmen}
      className={`mm-karte-rahmen${ziehen?.aktiv ? ' mm-zieht' : ''}`}
      tabIndex={0}
      onKeyDown={taste}
      aria-label="Mindmap – Pfeiltasten zum Wandern, Enter neuer Punkt daneben, Tab neuer Unterpunkt, Entf löschen, F2 umbenennen"
    >
      <svg
        className="mm-flaeche"
        width={groesse.b}
        height={groesse.h}
        onPointerDown={runter}
        onPointerMove={bewegen}
        onPointerUp={hoch}
        onPointerCancel={hoch}
        onDoubleClick={(e) => {
          const id = (e.target as Element).closest('[data-knoten]')?.getAttribute('data-knoten');
          if (id) umbenennenStarten(id);
        }}
      >
        <g transform={`translate(${sicht.x},${sicht.y}) scale(${sicht.s})`}>
          <g className="mm-kanten">
            {anordnung.liste.map((l) => {
              const k = baum.nachId.get(l.id);
              const e = k?.elternId ? anordnung.lagen.get(k.elternId) : undefined;
              if (!e) return null;
              const verschoben = ziehen?.aktiv && (ziehen.id === l.id || nachkommen(z.baum, ziehen.id).includes(l.id));
              return (
                <path
                  key={l.id}
                  d={kante(e, l)}
                  stroke={ton(l.id)}
                  strokeWidth={Math.max(1.4, 3.6 - l.tiefe * 0.7)}
                  fill="none"
                  opacity={verschoben ? 0.3 : 0.85}
                />
              );
            })}
          </g>
          {anordnung.liste.map((l) => {
            const k = baum.nachId.get(l.id)!;
            return (
              <KnotenBild
                key={l.id}
                l={l}
                k={k}
                ton={ton(l.id)}
                ausgewaehlt={auswahl === l.id}
                gedimmt={!!ziehen?.aktiv && (ziehen.id === l.id || nachkommen(z.baum, ziehen.id).includes(l.id))}
                ziel={ziehen?.aktiv && ziehen.ziel?.id === l.id ? ziehen.ziel.art : null}
                entwurf={!!bearb?.neu && bearb.id === l.id}
                andere={andere.filter((p) => p.knotenId === l.id)}
              />
            );
          })}
          {ziehen?.aktiv && (
            <g transform={`translate(${ziehen.wx - ziehen.dx},${ziehen.wy - ziehen.dy})`} opacity={0.75} pointerEvents="none">
              {(() => {
                const l = anordnung.lagen.get(ziehen.id);
                return l ? <rect width={l.b} height={l.h} rx={10} fill="var(--flaeche)" stroke="var(--akzent)" strokeWidth={2} strokeDasharray="5 4" /> : null;
              })()}
            </g>
          )}
        </g>
      </svg>

      {bearb && bl && (
        <input
          key={bearb.id}
          className="mm-inline"
          autoFocus
          value={bearb.text}
          placeholder={bearb.neu ? 'Neuer Punkt …' : ''}
          maxLength={500}
          style={{
            left: bl.x * sicht.s + sicht.x,
            top: bl.y * sicht.s + sicht.y,
            width: Math.max(bl.b, 120) * sicht.s,
            height: bl.h * sicht.s,
            fontSize: (bl.seite === 0 ? MASS.schriftWurzel : MASS.schrift) * sicht.s,
          }}
          onChange={(e) => setBearb({ ...bearb, text: e.target.value })}
          onBlur={() => {
            // Nur die Bearbeitung abschließen, zu der dieses Feld gehört (nicht eine schon begonnene neue)
            const id = bearb.id;
            setTimeout(() => bearbRef.current?.id === id && fertig());
          }}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') {
              e.preventDefault();
              fertig();
            } else if (e.key === 'Tab') {
              e.preventDefault();
              fertig('kind');
            } else if (e.key === 'Escape') {
              e.preventDefault();
              abbrechen();
            }
          }}
          onFocus={(e) => e.target.select()}
        />
      )}

      <div className="mm-zoom" onPointerDown={(e) => e.stopPropagation()}>
        <button type="button" className="knopf knopf-klein" title="Vergrößern (+)" onClick={() => zoomen(1.25)}>
          +
        </button>
        <button type="button" className="knopf knopf-klein" title="Verkleinern (−)" onClick={() => zoomen(1 / 1.25)}>
          −
        </button>
        <button type="button" className="knopf knopf-klein" title="Alles zeigen (0)" onClick={einpassen}>
          ⤢
        </button>
        <span className="leise klein mm-zoom-wert">{Math.round(sicht.s * 100)} %</span>
      </div>
    </div>
  );
}

function KnotenBild({
  l,
  k,
  ton,
  ausgewaehlt,
  gedimmt,
  ziel,
  entwurf,
  andere,
}: {
  l: Lage;
  k: MindmapKnoten;
  ton: string;
  ausgewaehlt: boolean;
  gedimmt: boolean;
  ziel: 'kind' | 'davor' | 'danach' | null;
  entwurf: boolean;
  andere: { name: string; benutzerId: string; bearbeitet: boolean }[];
}) {
  const wurzel = l.seite === 0;
  const eigen = k.farbe ? FARBTON[k.farbe] : null;
  const fuellung = wurzel ? (eigen ?? 'var(--akzent)') : eigen ? `${eigen}30` : 'var(--flaeche)';
  const textFarbe = wurzel ? 'var(--akzent-text)' : 'var(--text)';
  const zeile = wurzel ? MASS.zeileWurzel : MASS.zeile;
  const tx = MASS.innenX + (k.symbol ? MASS.symbol + 4 : 0) + (wurzel ? 6 : 0);
  const ty = MASS.innenY + (wurzel ? 5 : 0);
  let rx = l.b - MASS.innenX + 4;
  const zeichen: React.ReactNode[] = [];
  if (k.anlagen) {
    rx -= MASS.zeichen;
    zeichen.push(
      <g key="a" transform={`translate(${rx},${l.h / 2 - 8})`} className="mm-zeichen">
        <title>{`${k.anlagen} Anlage${k.anlagen === 1 ? '' : 'n'}`}</title>
        <path d={KLAMMER_PFAD} transform="scale(0.62)" fill="none" stroke={wurzel ? textFarbe : ton} strokeWidth={2.6} strokeLinecap="round" />
        <text x={16} y={12} fontSize={11} fill={textFarbe} opacity={0.8}>
          {k.anlagen}
        </text>
      </g>,
    );
  }
  if (k.notizen || k.beschreibung) {
    rx -= MASS.zeichen - 6;
    zeichen.push(
      <g key="n" transform={`translate(${rx},${l.h / 2 - 8})`} className="mm-zeichen">
        <title>{k.notizen ? `${k.notizen} Notiz${k.notizen === 1 ? '' : 'en'}` : 'Mit Beschreibung'}</title>
        <path d={NOTIZ_PFAD} transform="scale(0.66)" fill="none" stroke={wurzel ? textFarbe : ton} strokeWidth={2.4} strokeLinejoin="round" />
      </g>,
    );
  }
  const person = andere[0];
  return (
    <g
      transform={`translate(${l.x},${l.y})`}
      data-knoten={entwurf ? undefined : l.id}
      className={`mm-knoten${ausgewaehlt ? ' mm-ausgewaehlt' : ''}`}
      opacity={gedimmt ? 0.35 : 1}
    >
      {ausgewaehlt && <rect x={-4} y={-4} width={l.b + 8} height={l.h + 8} rx={13} fill="none" stroke="var(--akzent)" strokeWidth={2.5} />}
      {person && (
        <>
          <rect x={-3} y={-3} width={l.b + 6} height={l.h + 6} rx={12} fill="none" stroke={personFarbe(person.benutzerId)} strokeWidth={2} strokeDasharray="5 3" />
          <text x={2} y={-8} fontSize={11} fontWeight={600} fill={personFarbe(person.benutzerId)}>
            {andere.map((p) => p.name).join(', ')}
            {person.bearbeitet ? ' bearbeitet …' : ''}
          </text>
        </>
      )}
      <rect
        width={l.b}
        height={l.h}
        rx={wurzel ? 14 : 10}
        fill={fuellung}
        stroke={ziel === 'kind' ? 'var(--akzent)' : wurzel ? 'none' : ton}
        strokeWidth={ziel === 'kind' ? 3 : 1.5}
        strokeDasharray={entwurf ? '4 3' : undefined}
      />
      {ziel === 'davor' && <line x1={0} x2={l.b} y1={-6} y2={-6} stroke="var(--akzent)" strokeWidth={3} strokeLinecap="round" />}
      {ziel === 'danach' && <line x1={0} x2={l.b} y1={l.h + 6} y2={l.h + 6} stroke="var(--akzent)" strokeWidth={3} strokeLinecap="round" />}
      {k.symbol && (
        <path
          d={SYMBOL_PFAD[k.symbol]}
          transform={`translate(${MASS.innenX + (wurzel ? 6 : 0)},${l.h / 2 - 10}) scale(${20 / 24})`}
          fill="none"
          stroke={wurzel ? textFarbe : ton}
          strokeWidth={2.1}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      <text x={tx} y={ty} fill={textFarbe} fontSize={wurzel ? MASS.schriftWurzel : MASS.schrift} fontWeight={wurzel ? 600 : 500} className="mm-text">
        {l.zeilen.map((z, i) => (
          <tspan key={i} x={tx} y={ty + i * zeile + zeile * 0.74}>
            {z}
          </tspan>
        ))}
      </text>
      {zeichen}
      {l.hatKinder && !wurzel && (
        <g
          data-klapp="1"
          className={`mm-klapp${l.eingeklappt ? ' mm-klapp-zu' : ''}`}
          transform={`translate(${l.seite === -1 ? -9 : l.b + 9},${l.h / 2})`}
        >
          <title>{l.eingeklappt ? 'Ausklappen' : 'Einklappen'}</title>
          <circle r={l.eingeklappt ? 10 : 7} fill="var(--flaeche)" stroke={ton} strokeWidth={1.5} />
          {l.eingeklappt ? (
            <text textAnchor="middle" y={4} fontSize={10.5} fontWeight={700} fill={ton}>
              {l.versteckt > 99 ? '99+' : l.versteckt}
            </text>
          ) : (
            <line x1={-3.5} x2={3.5} y1={0} y2={0} stroke={ton} strokeWidth={1.8} strokeLinecap="round" />
          )}
        </g>
      )}
    </g>
  );
}
